import React, { useState, useEffect, useRef } from 'react';
import html2pdf from 'html2pdf.js';

interface SlidePreviewProps {
  slideHtml: string;
}

const SlidePreview: React.FC<SlidePreviewProps> = ({ slideHtml }) => {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [showFullscreen, setShowFullscreen] = useState(false);
  const [copied, setCopied] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [animateIn, setAnimateIn] = useState(false);

  useEffect(() => {
    // Trigger entrance animation
    const timer = setTimeout(() => setAnimateIn(true), 300);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const checkForOverflow = () => {
      if (iframeRef.current) {
        try {
          const iframeDocument = iframeRef.current.contentDocument || iframeRef.current.contentWindow?.document;
          if (iframeDocument) {
            const bodyElement = iframeDocument.body;
            
            // Give the iframe content some time to render
            setTimeout(() => {
              const isContentOverflowing = 
                bodyElement.scrollHeight > iframeRef.current!.clientHeight || 
                bodyElement.scrollWidth > iframeRef.current!.clientWidth;
              
              setIsOverflowing(isContentOverflowing);
            }, 500);
          }
        } catch (error) {
          console.error("Error checking for overflow:", error);
        }
      }
    };
    
    // Only check for overflow if we have content
    if (slideHtml) {
      // Wait for the iframe to load
      setTimeout(checkForOverflow, 1000);
      
      // Also check on window resize
      window.addEventListener('resize', checkForOverflow);
      return () => window.removeEventListener('resize', checkForOverflow);
    }
  }, [slideHtml]);
  
  const exportToPDF = () => {
    const element = iframeRef.current?.contentDocument?.documentElement;
    if (!element) return;
    
    const opt = {
      margin: 0,
      filename: 'slides.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'px', format: [1920, 1080], orientation: 'landscape' }
    };
    
    html2pdf().set(opt).from(element).save();
  };
  
  const downloadHTML = () => {
    const blob = new Blob([slideHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'slides.html';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  
  const copyHTML = () => {
    navigator.clipboard.writeText(slideHtml).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  
  if (!slideHtml) {
    return null;
  }
  
  return (
    <div className={`mt-8 transition-all duration-1000 ease-out ${animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
      <div className="relative z-10 mb-6">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-violet-500 via-teal-500 to-violet-500 transform scale-x-100"></div>
        <h2 className="text-3xl font-bold text-white pt-6 flex items-center">
          <div className="w-10 h-10 rounded-xl mr-3 bg-gradient-to-br from-violet-600 to-purple-800 flex items-center justify-center shadow-lg">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z"></path>
            </svg>
          </div>
          Your Presentation
        </h2>
      </div>
      
      <div className="glassmorphism rounded-2xl border border-white/10 shadow-2xl mb-8 overflow-hidden">
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-violet-600/5 via-transparent to-teal-600/5 pointer-events-none z-0"></div>
          
          <div className="flex justify-between items-center p-4 bg-gray-900/80 border-b border-white/5">
            <div className="flex space-x-2">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
            </div>
            <div className="text-white/70 text-sm font-medium">Presentation Preview</div>
            <div className="flex space-x-3">
              <button
                onClick={copyHTML}
                className="group relative flex items-center justify-center w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 hover:text-teal-300 transition-all duration-200"
                title="Copy HTML"
              >
                {copied ? (
                  <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"></path>
                  </svg>
                )}
                <span className="absolute right-full mr-2 whitespace-nowrap text-sm rounded bg-gray-800 text-white px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                  {copied ? 'Copied!' : 'Copy HTML'}
                </span>
              </button>
              <button
                onClick={() => setShowFullscreen(true)}
                className="group relative flex items-center justify-center w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 hover:text-teal-300 transition-all duration-200"
                title="View fullscreen"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"></path>
                </svg>
                <span className="absolute right-full mr-2 whitespace-nowrap text-sm rounded bg-gray-800 text-white px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                  Fullscreen
                </span>
              </button>
            </div>
          </div>
          
          <div className="relative bg-white h-[600px] overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent pointer-events-none"></div>
            
            <iframe
              ref={iframeRef}
              srcDoc={slideHtml}
              title="Slide Preview"
              className="w-full h-full border-0"
              sandbox="allow-same-origin"
              style={{ 
                width: '100%', 
                height: '100%',
                transformOrigin: 'top left',
              }}
              onLoad={() => {
                // After loading, add custom style to scale the content down if needed
                try {
                  const iframe = iframeRef.current;
                  if (iframe && iframe.contentDocument) {
                    const styleElement = iframe.contentDocument.createElement('style');
                    styleElement.textContent = `
                      @media screen {
                        body {
                          transform: scale(${iframe.clientWidth / 1920});
                          transform-origin: top left;
                          width: 1920px;
                          height: 1080px;
                          overflow: hidden;
                        }
                        .slide {
                          width: 1920px;
                          height: 1080px;
                          overflow: hidden;
                        }
                      }
                    `;
                    iframe.contentDocument.head.appendChild(styleElement);
                  }
                } catch (error) {
                  console.error("Error scaling iframe content:", error);
                }
              }}
            />
            
            {isOverflowing && (
              <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-red-900/90 to-red-900/20 text-white text-center text-sm backdrop-blur-sm">
                <div className="flex items-center justify-center space-x-2">
                  <svg className="w-5 h-5 text-red-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                  </svg>
                  <span>Content overflow detected. Some parts of your presentation may be cut off when viewed or printed.</span>
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className="p-4 bg-gray-900/80 border-t border-white/5 grid grid-cols-2 md:grid-cols-4 gap-2">
          <div className="col-span-2 md:col-span-1">
            <button
              onClick={exportToPDF}
              className="w-full bg-gradient-to-r from-violet-600 to-purple-700 hover:from-violet-500 hover:to-purple-600 text-white rounded-lg py-2.5 px-4 flex items-center justify-center transition-all duration-300 font-medium group relative overflow-hidden shadow-lg shadow-violet-900/20"
            >
              <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-teal-300/10 to-transparent group-hover:w-[200%] transition-all duration-700 ease-out -translate-x-full group-hover:translate-x-0"></span>
              <span className="absolute -inset-px rounded-lg border border-white/10 group-hover:border-white/20 transition-colors duration-300"></span>
              <svg className="w-5 h-5 mr-2 relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
              </svg>
              <span className="relative z-10">Export to PDF</span>
            </button>
          </div>
          
          <div className="col-span-2 md:col-span-1">
            <button
              onClick={downloadHTML}
              className="w-full bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-500 hover:to-teal-600 text-white rounded-lg py-2.5 px-4 flex items-center justify-center transition-all duration-300 font-medium group relative overflow-hidden shadow-lg shadow-teal-900/20"
            >
              <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-violet-300/10 to-transparent group-hover:w-[200%] transition-all duration-700 ease-out -translate-x-full group-hover:translate-x-0"></span>
              <span className="absolute -inset-px rounded-lg border border-white/10 group-hover:border-white/20 transition-colors duration-300"></span>
              <svg className="w-5 h-5 mr-2 relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
              <span className="relative z-10">Download HTML</span>
            </button>
          </div>
          
          <div className="col-span-2 hidden md:block">
            <div className="text-xs text-white/50 px-4 h-full flex items-center justify-end">
              <div className="flex items-center">
                <span>Powered by</span>
                <span className="ml-2 text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-violet-500 font-bold">SlideMage</span>
                <svg className="w-4 h-4 ml-1 text-teal-400" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21.66 10.44l-9.13-8.14a.64.64 0 00-.86 0l-9.13 8.14a.64.64 0 00.43 1.12h2.14v7.79c0 .36.3.65.65.65h3.24a.65.65 0 00.65-.65v-4.62h5.23v4.62c0 .36.29.65.65.65h3.24a.65.65 0 00.65-.65v-7.79H21.2c.29 0 .55-.16.62-.42a.64.64 0 00-.16-.7z" fill="currentColor"/>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {showFullscreen && (
        <div className="fixed inset-0 bg-black/80 z-50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-6xl h-[90vh] bg-white rounded-xl overflow-hidden shadow-2xl relative">
            <div className="absolute top-4 right-4 z-10">
              <button 
                onClick={() => setShowFullscreen(false)}
                className="bg-gray-800/80 hover:bg-gray-700 text-white rounded-full p-2 backdrop-blur-sm"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
            <iframe
              srcDoc={slideHtml}
              title="Fullscreen Slide Preview"
              className="w-full h-full border-0"
              sandbox="allow-same-origin"
              onLoad={(e) => {
                // Add scaling to fullscreen preview too
                try {
                  const iframe = e.currentTarget;
                  if (iframe && iframe.contentDocument) {
                    const styleElement = iframe.contentDocument.createElement('style');
                    styleElement.textContent = `
                      @media screen {
                        body, html {
                          margin: 0;
                          padding: 0;
                          display: flex;
                          justify-content: center;
                          align-items: center;
                          width: 100%;
                          height: 100%;
                          background: #f0f0f0;
                        }
                        body {
                          max-width: 1920px;
                          max-height: 1080px;
                          transform: scale(${Math.min(iframe.clientWidth / 1920, iframe.clientHeight / 1080, 1)});
                          transform-origin: center;
                          overflow: visible;
                        }
                      }
                    `;
                    iframe.contentDocument.head.appendChild(styleElement);
                  }
                } catch (error) {
                  console.error("Error scaling fullscreen iframe content:", error);
                }
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SlidePreview; 